import dayjs from "dayjs";

type StringDate = string | Date;

export const getDateNow = (): string => dayjs().toISOString();
export const getDateEndOfDay = (date?: StringDate): string => dayjs(date).endOf("day").toISOString();
export const getDateStartOfDay = (date?: StringDate): string => dayjs(date).startOf("day").toISOString();
export const getDateDurationMsHumanized = (milliseconds?: number | null): string | null => {
  if (!milliseconds) return null;
  return dayjs.duration(Math.abs(milliseconds)).humanize();
};
export const getDateDurationMsExact = (milliseconds?: number | null): string | null => {
  if (!milliseconds) return null;
  const duration = dayjs.duration(Math.abs(milliseconds));
  const formattedDuration = duration.format("Y[y] M[mo] D[d] H[h] m[m] s[s]");
  const escapedDuration = formattedDuration
    .replace(/\b0y\b/, "")
    .replace(/\b0mo\b/, "")
    .replace(/\b0d\b/, "")
    .replace(/\b0h\b/, "")
    .replace(/\b0m\b/, "")
    .replace(/\b0s\b/, "");
  return escapedDuration.trim();
};

export const getDateDurationHumanized = (date_1: StringDate, date_2: StringDate): string =>
  dayjs.duration(Math.abs(dayjs(date_1).diff(date_2))).humanize();
export const getDateDurationExact = (date_1: StringDate, date_2: StringDate): string => {
  const duration = dayjs.duration(Math.abs(dayjs(date_1).diff(date_2)));
  const formattedDuration = duration.format("Y[y] M[mo] D[d] H[h] m[m] s[s]");
  const escapedDuration = formattedDuration
    .replace(/\b0y\b/, "")
    .replace(/\b0mo\b/, "")
    .replace(/\b0d\b/, "")
    .replace(/\b0h\b/, "")
    .replace(/\b0m\b/, "")
    .replace(/\b0s\b/, "");
  return escapedDuration.trim();
};
export const getDateAgoHumanized = (date: StringDate): string => dayjs().to(dayjs(date));
export const getDatePlusMinus = (date?: StringDate, add = 0, unit: dayjs.ManipulateType = "day"): string =>
  dayjs(date).add(add, unit).toISOString();
export const getDateStartOfTomorrow = (date?: StringDate): string =>
  dayjs(getDatePlusMinus(date, 1)).startOf("day").toISOString();

export const getDateIsBefore = (date_1: StringDate, date_2: StringDate): boolean =>
  dayjs(date_1).isBefore(dayjs(date_2));
export const getDateIsValid = (date?: StringDate | null): boolean => (date ? dayjs(date).isValid() : false);
export const getDateISO = (date?: StringDate): string => dayjs(date).toISOString();
export const getDateJS = (date?: StringDate): Date => dayjs(date).toDate();
export const getDateUnixTime = (date: StringDate): number => dayjs(date).unix();
export const getDateDaysInThePast = (days = 30): string => dayjs().subtract(days, "days").toISOString();
export const getDateDaysInTheFuture = (days = 30, date?: StringDate | null): string =>
  dayjs(date).add(days, "days").toISOString();
export const getDateDaysDifferenceToNow = (date: StringDate): number => dayjs(date).diff(getDateNow(), "day", true);
export const getDateIsToday = (date: StringDate): boolean => dayjs(date).isSame(getDateNow(), "day");
export const getDateIsSameDay = (date_1: StringDate, date_2: StringDate): boolean =>
  dayjs(date_1).isSame(date_2, "day");

export type DateFormat =
  | "datetime"
  | "datetime-short"
  | "date"
  | "date-short"
  | "date-short-file"
  | "date-very-short"
  | "time"
  | "time-short";
export const DATE_FORMATS: Record<DateFormat, string> = {
  datetime: "MMM D, YYYY HH:mm", // -> Dec 11, 2024 00:00
  "datetime-short": "DD.MM.YYYY HH:mm", // -> 11.12.2024 00:00
  date: "MMM D, YYYY", // -> Dec 11, 2024
  "date-short": "DD.MM.YYYY", // -> 11.12.2024
  "date-short-file": "DD-MM-YYYY", // -> 11.12.2024
  "date-very-short": "DD.MM.", // -> 11.12.
  time: "HH:mm:ss", // 00:00:00
  "time-short": "HH:mm", // 00:00
};

export const getDateFormatted = (date: StringDate, format: DateFormat = "date-short") =>
  dayjs(date).format(DATE_FORMATS[format]);

interface GenerateDayDateArrayParams {
  from: StringDate;
  to?: StringDate;
}
export function generateDayDateArray({ from, to }: GenerateDayDateArrayParams): string[] {
  const _from = getDateStartOfDay(from);
  const _to = to ? getDateEndOfDay(to) : getDateEndOfDay();

  const dates: string[] = [];
  for (let date = _from; getDateIsBefore(date, _to); date = getDatePlusMinus(date, 1, "day")) {
    dates.push(date);
  }

  return dates;
}
